import type { ParticleMaterialProfile } from './profiles';
import { DEFAULT_SURFACE_NORMAL } from './impactFrame';
import type { ImpactKind, ImpactParams } from './impactRegime';

export interface BoxCollider {
  center: readonly [number, number, number];
  half: readonly [number, number, number];
}

export interface ArenaBounds {
  minX: number;
  maxX: number;
  minZ: number;
  maxZ: number;
}

/** Heightfield XZ domain (min + size). Derived from ArenaBounds at world create. */
export interface ArenaXZ {
  minX: number;
  minZ: number;
  sizeX: number;
  sizeZ: number;
}

export type ParticleTier = 'desktop' | 'mobile';

export interface ParticleWorldOptions {
  arenaBounds: ArenaBounds;
  tier: ParticleTier;
  colliders: BoxCollider[];
  /**
   * Chunks/chips collide and settle only on the yard floor (y≈0). The wall
   * AABB + raised base elevation are ignored so debris cannot freeze on the
   * ghost roof or faces. Particle-lab default is off (chunks perch on boxes).
   */
  chunkFloorOnlySettle?: boolean;
  /**
   * Skip the CPU chunk/chip instanced meshes entirely. Demolition uses this
   * so Box3D hulls own every solid fragment; ParticleLab/SiegeLab omit it.
   */
  disableChunks?: boolean;
  /**
   * CPU chunk instanced-mesh capacity. Omit for ParticleLab/SiegeLab (48).
   * `0` skips the chunk sim — same budget as `chunkCapacityForTier('mobile')`.
   */
  chunkCapacity?: number;
  /**
   * CPU chip instanced-mesh capacity. Omit for ParticleLab/SiegeLab (64).
   * `0` disables chips.
   */
  chipCapacity?: number;
  /**
   * Grain GPGPU capacity. Omit for ParticleLab/SiegeLab (tier default:
   * 65536 desktop / 16384 mobile). `0` skips the grain sim — demolition
   * uses this so airborne grit never mounts.
   */
  grainCapacity?: number;
  /**
   * Fines / cloud GPGPU capacity (cloudSim `particle-lab-fines` pool).
   * Omit for ParticleLab/SiegeLab (tier default: 4096 desktop / 1024 mobile).
   * `0` skips the cloud sim — demolition uses this so airborne fine-points
   * never mount. Heightfield still mounts; leftover fine mass stays on the
   * deposit ledger without extra stamps.
   */
  fineCapacity?: number;
  /** Cloud-shadow splat. Omit for particle-lab defaults (0.20 / 2.4 / 18). */
  groundShadow?: {
    splatAmt?: number;
    pointScale?: number;
    maxPoint?: number;
  };
}

export interface BurstParams {
  position: readonly [number, number, number] | { x: number; y: number; z: number };
  direction: readonly [number, number, number] | { x: number; y: number; z: number };
  energy: number;
  mass: number;
  profile: ParticleMaterialProfile;
  /** Unit hit-face normal. Default [0,1,0] keeps the floor mushroom model. */
  surfaceNormal?: readonly [number, number, number] | { x: number; y: number; z: number };
  /**
   * Impact regime. Omitted or `kind:'cannon'` is the calibration identity
   * (intensity 0.25, blast 0) — spawn records match the pre-regime planners.
   */
  impact?: ImpactParams;
}

export interface ParticleStats {
  liveGrains: number;
  liveFines: number;
  liveChunks: number;
  airborneChunks: number;
  settledChunks: number;
  bleedEmissions: number;
  jointPuffEmissions: number;
  simTime: number;
  pileVolume: number;
  bursts: number;
  frame: number;
  capacity: number;
  fineCapacity: number;
  lastStepMs: number;
  pendingSpawns: number;
  budgetPressure: number;
  sixWay: boolean;
  tier: ParticleTier;
}

export interface LastBurstCall {
  position: [number, number, number];
  direction: [number, number, number];
  energy: number;
  mass: number;
  profileId: ParticleMaterialProfile['id'];
  grains: number;
  fines: number;
  chunks: number;
  grainWanted: number;
  fineWanted: number;
  chunkWanted: number;
  surfaceNormal: [number, number, number];
  kind: ImpactKind;
  intensity: number;
  blastFraction: number;
  backSpallFines: number;
  backSpallChips: number;
  ringFines: number;
}

export function asTriple(
  v: readonly [number, number, number] | { x: number; y: number; z: number },
): [number, number, number] {
  if ('x' in v) return [v.x, v.y, v.z];
  return [v[0], v[1], v[2]];
}

export { DEFAULT_SURFACE_NORMAL };

export function asNormalTriple(
  v?: readonly [number, number, number] | { x: number; y: number; z: number },
): [number, number, number] {
  if (!v) return [DEFAULT_SURFACE_NORMAL[0], DEFAULT_SURFACE_NORMAL[1], DEFAULT_SURFACE_NORMAL[2]];
  return asTriple(v);
}
